import { Link } from 'react-router-dom';

export interface ContentItem {
  title: string;
  desc: string;
  image?: string;
  icon?: string;
  href?: string;
  tag?: string;
}

export interface ContentSectionData {
  badge: string;
  heading: string;
  headingAccent?: string;
  subheading?: string;
  items: ContentItem[];
  ctaLabel?: string;
  ctaHref?: string;
  layout?: 'grid-3' | 'grid-2' | 'alternating';
  darkBg?: boolean;
}

interface Props { content: ContentSectionData; }

function SectionHeader({ content, headingColor, bodyColor }: { content: ContentSectionData; headingColor: string; bodyColor: string }) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-end gap-6 mb-16">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-gold"></div>
          <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-gold">{content.badge}</span>
        </div>
        <h2
          className={`font-serif font-semibold leading-tight ${headingColor}`}
          style={{ fontSize: 'clamp(2.4rem, 4vw, 3.5rem)' }}
        >
          {content.heading}
          {content.headingAccent && (
            <em className="text-gold italic block font-light" style={{ fontSize: '0.85em' }}>{content.headingAccent}</em>
          )}
        </h2>
      </div>
      {content.subheading && (
        <p className={`font-sans text-sm leading-relaxed max-w-xs ${bodyColor}`}>{content.subheading}</p>
      )}
    </div>
  );
}

function FeaturedGrid({ content }: Props) {
  const [featured, ...rest] = content.items;
  return (
    <div>
      {/* FEATURED PROJECT — full-width cinematic banner */}
      {featured && (
        <div className="relative w-full rounded-2xl overflow-hidden mb-6 group cursor-pointer" style={{ height: '520px' }}>
          {featured.image && (
            <img
              src={featured.image}
              alt={featured.title}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/95 via-forest-deep/65 to-forest-deep/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/70 via-transparent to-transparent"></div>

          {/* Tag badge */}
          {featured.tag && (
            <div className="absolute top-8 left-8">
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase bg-gold text-forest-deep font-bold px-3 py-1.5 rounded-full">{featured.tag}</span>
            </div>
          )}

          {/* Content overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
            <h3
              className="font-serif text-white font-semibold leading-tight mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              {featured.title}
            </h3>
            <p className="font-sans text-white/65 text-base leading-relaxed mb-6 max-w-lg">{featured.desc}</p>
            {featured.href && (
              <Link
                to={featured.href}
                onClick={(e) => e.stopPropagation()}
                className="whitespace-nowrap inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm rounded-full px-6 py-3 transition-all cursor-pointer"
              >
                Explore Service <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Remaining cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {rest.map((item, i) => (
          <div key={i} className="group bg-white rounded-2xl overflow-hidden border border-stone/40 hover:border-gold/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl cursor-pointer"
            onClick={() => item.href && (window.location.href = item.href)}>
            {item.image && (
              <div className="w-full overflow-hidden" style={{ height: '200px' }}>
                <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
            )}
            <div className="p-6">
              <h3 className="font-serif text-xl text-forest font-semibold mb-2">{item.title}</h3>
              <p className="font-sans text-sm text-charcoal-muted leading-relaxed mb-4">{item.desc}</p>
              {item.href && (
                <Link to={item.href} onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 text-gold text-sm font-semibold font-sans group-hover:gap-2.5 transition-all cursor-pointer">
                  Learn More <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlternatingLayout({ content, headingColor, bodyColor }: Props & { headingColor: string; bodyColor: string }) {
  return (
    <div className="space-y-24">
      {content.items.map((item, i) => (
        <div key={i} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
          <div className={`relative rounded-2xl overflow-hidden ${i % 2 === 1 ? 'lg:order-2' : ''}`} style={{ height: '440px' }}>
            {item.image && (
              <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" />
            )}
            {item.tag && (
              <span className="absolute top-6 left-6 font-sans text-[10px] tracking-[0.2em] uppercase bg-gold text-forest-deep font-bold px-3 py-1.5 rounded-full">{item.tag}</span>
            )}
          </div>
          <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
            <div className="h-px w-10 bg-gold mb-6"></div>
            <h3 className={`font-serif font-semibold leading-tight mb-5 ${headingColor}`} style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)' }}>
              {item.title}
            </h3>
            <p className={`font-sans text-base leading-relaxed mb-8 ${bodyColor}`}>{item.desc}</p>
            {item.href && (
              <Link to={item.href} className="whitespace-nowrap inline-flex items-center gap-2 border border-forest text-forest font-semibold text-sm rounded-full px-6 py-3 hover:bg-forest hover:text-white transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function GridLayout({ content, cols, headingColor, bodyColor }: Props & { cols: number; headingColor: string; bodyColor: string }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 ${cols >= 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
      {content.items.map((item, i) => (
        <div key={i} className="group bg-white rounded-2xl overflow-hidden border border-stone/40 hover:border-gold/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
          {item.image && (
            <div className="w-full overflow-hidden" style={{ height: '220px' }}>
              <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
            </div>
          )}
          <div className="p-6">
            {!item.image && item.icon && (
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/10 mb-4">
                <i className={`${item.icon} text-gold text-lg`}></i>
              </div>
            )}
            <h3 className={`font-serif text-xl font-semibold mb-2 ${headingColor}`}>{item.title}</h3>
            <p className={`font-sans text-sm leading-relaxed mb-4 ${bodyColor}`}>{item.desc}</p>
            {item.href && (
              <Link to={item.href} className="inline-flex items-center gap-1.5 text-gold text-sm font-semibold font-sans hover:gap-2.5 transition-all cursor-pointer">
                Learn More <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ContentSection({ content }: Props) {
  const { layout = 'grid-3', darkBg = false } = content;
  const bg = darkBg ? 'bg-charcoal' : 'bg-cream';
  const headingColor = darkBg ? 'text-white' : 'text-forest';
  const bodyColor = darkBg ? 'text-white/55' : 'text-charcoal-muted';

  return (
    <section className={`w-full py-28 ${bg}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionHeader content={content} headingColor={headingColor} bodyColor={bodyColor} />

        {layout === 'alternating' ? (
          <AlternatingLayout content={content} headingColor={headingColor} bodyColor={bodyColor} />
        ) : layout === 'grid-3' && content.items.length >= 4 ? (
          <FeaturedGrid content={content} />
        ) : (
          <GridLayout content={content} cols={layout === 'grid-3' ? 3 : 2} headingColor={headingColor} bodyColor={bodyColor} />
        )}

        {content.ctaLabel && content.ctaHref && (
          <div className="mt-14 text-center">
            <Link to={content.ctaHref} className="whitespace-nowrap inline-flex items-center gap-2 bg-forest hover:bg-forest-mid text-white font-semibold text-sm rounded-full px-8 py-4 transition-all cursor-pointer">
              {content.ctaLabel} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
